import { ReactElement, useCallback } from 'react';
import { App, Form } from 'antd';
import { styled } from 'styled-components';
import ContentLayout from './ContentLayout';
import ContentHeader from './content/ContentHeader';
import ContentBody from './content/ContentBody';
import ContentFooter from './content/ContentFooter';
import useCreateAdvisors from '../hooks/useCreateAdvisors';
import { BulkCreateAdvisorsBody } from '@minutes-system/backend/src/api/advisors/schema';
import { useNavigate } from 'react-router-dom';
import { blockStyle } from '../../../../components/styles/Block';

const ContentBlock = styled.article`
  ${blockStyle}
  background: #fff;
  border-radius: 8px;
`;

interface AdvisorFormValue {
  name: string;
  phone: string;
  rowKey: number;
}

interface FormValues {
  advisors: AdvisorFormValue[];
}

const MIN_ROWS = 1;
const MAX_ROWS = 10;

const Content = (): ReactElement => {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const { results, createAdvisors } = useCreateAdvisors();
  const { message } = App.useApp();

  const onFinish = useCallback(
    async (values: FormValues) => {
      try {
        const body: BulkCreateAdvisorsBody = values.advisors.map(advisor => ({
          name: advisor.name.trim(),
          phone: advisor.phone,
          rowKey: advisor.rowKey,
        }));

        const response = await createAdvisors(body);
        const { success, error } = response.summary;
        const total = success + error;

        if (error === 0) {
          message.success('全ての営業担当者が正常に登録されました');
        } else if (success === 0) {
          message.error(
            '全ての営業担当者の登録に失敗しました。エラーがある行の詳細を確認し、必要に応じて修正してください。'
          );
        } else {
          message.warning(
            `${total.toString()}件中${success.toString()}件が正常に登録されました。${error.toString()}件にエラーがあります。エラーがある行の詳細を確認し、必要に応じて修正してください。`
          );
        }
      } catch (error) {
        console.error('Failed to create advisors:', error);
        message.error('営業担当者の登録に失敗しました');
      }
    },
    [createAdvisors, message]
  );

  const handleCancel = useCallback(() => {
    void navigate('/advisors');
  }, [navigate]);

  return (
    <ContentBlock>
      <Form<FormValues>
        form={form}
        onFinish={values => {
          void onFinish(values);
        }}
        initialValues={{
          advisors: [{ name: '', phone: '' }],
        }}
      >
        <Form.List name='advisors'>
          {(fields, { add, remove }) => (
            <ContentLayout
              header={
                <ContentHeader
                  rowCount={fields.length}
                  onAdd={() => {
                    add({ name: '', phone: '' });
                  }}
                  maxRows={MAX_ROWS}
                />
              }
              body={<ContentBody fields={fields} onRemove={remove} minRows={MIN_ROWS} results={results} />}
              footer={<ContentFooter total={fields.length} maxRows={MAX_ROWS} onCancel={handleCancel} />}
            />
          )}
        </Form.List>
      </Form>
    </ContentBlock>
  );
};

export default Content;
